import React, { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../services/api";
import Button from "../../common/Button";
import "./OtherExpenses.css";

const EXPENSE_CATEGORIES = [
  "Rent",
  "Salary",
  "Internet",
  "Electricity",
  "Other"
];

const RECURRING_FREQUENCIES = [
  "monthly",
  "bi_monthly",
  "quarterly",
  "yearly"
];

interface Expense {
  id: number;
  category: string;
  description?: string;
  amount: number;
  expense_date: string;
  payment_date?: string;
  is_recurring?: boolean;
  recurring_frequency?: string;
  notes?: string;
  period_month?: string;
  archived_at?: string | null;
}

interface FormData {
  category: string;
  description: string;
  amount: string;
  expense_date: string;
  payment_date: string;
  is_recurring: boolean;
  recurring_frequency: string;
  notes: string;
}

export default function OtherExpenses() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paidTab, setPaidTab] = useState<"all" | "paid" | "unpaid">("all");
  const [viewTab, setViewTab] = useState<"current" | "history">("current");
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState<Expense[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyStart, setHistoryStart] = useState("");
  const [historyEnd, setHistoryEnd] = useState("");

  const [formData, setFormData] = useState<FormData>({
    category: "Rent",
    description: "",
    amount: "",
    expense_date: new Date().toISOString().split("T")[0],
    payment_date: "",
    is_recurring: false,
    recurring_frequency: "monthly",
    notes: ""
  });

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let url = "/api/admin/expenses";
      const params = new URLSearchParams();
      if (filterCategory) params.append("category", filterCategory);
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (params.toString()) url += "?" + params.toString();

      const data = await apiGet(url) as Expense[];
      setExpenses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load expenses", err);
      setError("Failed to load expenses");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, startDate, endDate]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setError(null);
    try {
      let url = "/api/admin/expenses/history";
      const params = new URLSearchParams();
      if (historyStart) params.append("start_date", historyStart);
      if (historyEnd) params.append("end_date", historyEnd);
      if (params.toString()) url += "?" + params.toString();
      const data = await apiGet(url) as Expense[];
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load expense history", err);
      setError("Failed to load expense history");
    } finally {
      setHistoryLoading(false);
    }
  }, [historyStart, historyEnd]);

  const handleRefreshMonthly = async () => {
    if (!window.confirm("Run monthly refresh? This will archive non-recurring expenses and create new unpaid rows for recurring (monthly / bi-monthly / quarterly / yearly) for the current month. Continue?")) return;
    setRefreshing(true);
    setError(null);
    try {
      await apiPost("/api/admin/expenses/refresh-monthly", {});
      loadExpenses();
    } catch (err: unknown) {
      console.error("Refresh monthly failed", err);
      setError((err as Error)?.message || "Refresh monthly failed");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  useEffect(() => {
    if (viewTab === "history") loadHistory();
  }, [viewTab, loadHistory]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const payload = {
      ...formData,
      amount: parseFloat(formData.amount || "0"),
      recurring_frequency: formData.is_recurring ? formData.recurring_frequency : null
    };

    try {
      if (editingExpense) {
        await apiPut(`/api/admin/expenses/${editingExpense.id}`, payload);
      } else {
        const { payment_date: _pd, ...createPayload } = payload;
        await apiPost("/api/admin/expenses", createPayload);
      }

      setShowForm(false);
      setEditingExpense(null);
      resetForm();
      loadExpenses();
    } catch (err: unknown) {
      console.error("Failed to save expense", err);
      const msg = (err as Error)?.message || "Failed to save expense";
      setError(msg.includes("expenses table not found") || msg.includes("migrate-docker")
        ? `${msg} Data is stored in PostgreSQL table expenses (clinic_db).`
        : msg);
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      description: expense.description || "",
      amount: expense.amount.toString(),
      expense_date: expense.expense_date,
      payment_date: expense.payment_date || "",
      is_recurring: expense.is_recurring || false,
      recurring_frequency: expense.recurring_frequency || "monthly",
      notes: expense.notes || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this expense?")) {
      return;
    }

    try {
      await apiDelete(`/api/admin/expenses/${id}`);
      loadExpenses();
    } catch (err: unknown) {
      console.error("Failed to delete expense", err);
      setError((err as Error)?.message || "Failed to delete expense");
    }
  };

  const handleMarkAsPaid = async (expense: Expense) => {
    const confirmMessage = `Mark this expense as paid?\n\n` +
      `Category: ${expense.category}\n` +
      `Amount: ₹${expense.amount.toLocaleString("en-IN")}\n` +
      `Description: ${expense.description || "N/A"}\n\n` +
      `This will add it to expenditure calculations.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await apiPost(`/api/admin/expenses/${expense.id}/mark-paid`, {
        payment_date: new Date().toISOString().split("T")[0]
      });
      loadExpenses();
    } catch (err: unknown) {
      console.error("Failed to mark expense as paid", err);
      setError((err as Error)?.message || "Failed to mark expense as paid");
    }
  };

  const resetForm = () => {
    setFormData({
      category: "Rent",
      description: "",
      amount: "",
      expense_date: new Date().toISOString().split("T")[0],
      payment_date: "",
      is_recurring: false,
      recurring_frequency: "monthly",
      notes: ""
    });
    setEditingExpense(null);
  };

  const formatCurrency = (amount: number | undefined) => {
    return `₹${(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  /** Treat null, undefined, empty string as unpaid; only real date = paid */
  const isPaid = (e: Expense) => Boolean(e.payment_date && String(e.payment_date).trim());

  const paidExpenses = expenses.filter(isPaid);
  const filteredExpenses = paidTab === "paid"
    ? expenses.filter(isPaid)
    : paidTab === "unpaid"
      ? expenses.filter(e => !isPaid(e))
      : expenses;

  const totalAmount = paidExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalsByCategory = paidExpenses.reduce<Record<string, number>>((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + (e.amount || 0);
    return acc;
  }, {});

  const unpaidAmount = expenses.filter(e => !isPaid(e)).reduce((sum, e) => sum + (e.amount || 0), 0);

  /** Group history by date (payment_date or expense_date) for date-wise display */
  const historyByDate = React.useMemo(() => {
    const map = new Map<string, Expense[]>();
    for (const e of history) {
      const d = (e.payment_date && String(e.payment_date).trim()) || e.expense_date || "";
      if (!d) continue;
      const list = map.get(d) || [];
      list.push(e);
      map.set(d, list);
    }
    const sorted = Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
    return sorted;
  }, [history]);

  return (
    <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', gap: '24px', minHeight: 'calc(100vh - 120px)' }}>
      <div className="mod-window-header" style={{ paddingBottom: 0, borderBottom: 'none', marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>💸 Other Expenses Management</h2>
            <p className="mod-text-muted">Log clinic operational costs, rent, salaries, and recurring bills.</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              variant="outline"
              onClick={handleRefreshMonthly}
              isLoading={refreshing}
              title="Archive non-recurring and create new unpaid rows for recurring (run on 1st of month)"
            >
              🔄 Refresh Monthly
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              ➕ Add Expense
            </Button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '6px', borderRadius: '12px', width: 'fit-content' }}>
        <button
          type="button"
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: 600, cursor: 'pointer', background: viewTab === "current" ? '#fff' : 'transparent', color: viewTab === "current" ? '#0f172a' : '#64748b', boxShadow: viewTab === "current" ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}
          onClick={() => setViewTab("current")}
        >
          📋 Current
        </button>
        <button
          type="button"
          style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: 600, cursor: 'pointer', background: viewTab === "history" ? '#fff' : 'transparent', color: viewTab === "history" ? '#0f172a' : '#64748b', boxShadow: viewTab === "history" ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}
          onClick={() => setViewTab("history")}
        >
          📅 Expense history
        </button>
      </div>

      {viewTab === "history" && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>From</label>
              <input
                type="date"
                value={historyStart}
                onChange={(e) => setHistoryStart(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>To</label>
              <input
                type="date"
                value={historyEnd}
                onChange={(e) => setHistoryEnd(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
            </div>
            <Button variant="outline" onClick={loadHistory} isLoading={historyLoading}>Load</Button>
          </div>

          {historyLoading && history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>Loading expense history...</div>
          ) : historyByDate.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#64748b', background: '#fff', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>No expense history in this range.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {historyByDate.map(([dateStr, list]) => (
                <div key={dateStr} style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'var(--mod-shadow-sm)' }}>
                  <h4 style={{ margin: 0, padding: '12px 16px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#0f172a', fontSize: '0.95rem' }}>{formatDate(dateStr)}</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead style={{ color: '#475569', textAlign: 'left' }}>
                      <tr>
                        <th style={{ padding: '12px 16px' }}>Category</th>
                        <th style={{ padding: '12px 16px' }}>Description</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right' }}>Amount</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center' }}>Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map(e => (
                        <tr key={e.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '12px 16px' }}><span style={{ background: '#e0f2fe', color: '#0284c7', padding: '4px 8px', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem' }}>{e.category}</span></td>
                          <td style={{ padding: '12px 16px', color: '#334155' }}>{e.description || "-"}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: '#dc2626' }}>{formatCurrency(e.amount)}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', color: '#64748b' }}>{isPaid(e) ? "✅ " + formatDate(e.payment_date) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {viewTab === "current" && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Summary Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', padding: '20px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase' }}>Total Paid Expenses</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#15803d', marginTop: '4px' }}>{formatCurrency(totalAmount)}</div>
              <div style={{ fontSize: '0.8rem', color: '#166534', opacity: 0.8, marginTop: '4px' }}>Included in expenditure</div>
            </div>
            {unpaidAmount > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '16px', padding: '20px' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase' }}>Total Unpaid Expenses</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#dc2626', marginTop: '4px' }}>{formatCurrency(unpaidAmount)}</div>
                <div style={{ fontSize: '0.8rem', color: '#991b1b', opacity: 0.8, marginTop: '4px' }}>Not in expenditure yet</div>
              </div>
            )}
            {Object.entries(totalsByCategory).slice(0, 3).map(([cat, amt]) => (
              <div key={cat} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '16px', padding: '20px' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>{cat} (Paid)</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1d4ed8', marginTop: '4px' }}>{formatCurrency(amt)}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {(['all', 'paid', 'unpaid'] as const).map(t => (
                <button
                  key={t}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: paidTab === t ? '#0f172a' : '#fff', color: paidTab === t ? '#fff' : '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                  onClick={() => setPaidTab(t)}
                >
                  {t === 'all' ? 'All' : t === 'paid' ? `Paid (${paidExpenses.length})` : `Unpaid (${expenses.length - paidExpenses.length})`}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select style={{ padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }} value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                <option value="">All Categories</option>
                {EXPENSE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <input type="date" style={{ padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }} value={startDate} onChange={(e) => setStartDate(e.target.value)} title="Start Date" />
              <input type="date" style={{ padding: '8px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }} value={endDate} onChange={(e) => setEndDate(e.target.value)} title="End Date" />
              {(filterCategory || startDate || endDate) && (
                <Button variant="ghost" size="sm" onClick={() => { setFilterCategory(""); setStartDate(""); setEndDate(""); }}>Clear</Button>
              )}
            </div>
          </div>

          {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', border: '1px solid #fecaca', fontSize: '0.9rem' }}>⚠️ {error}</div>}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => { setShowForm(false); resetForm(); }}>
          <div style={{ width: '100%', maxWidth: '500px', background: '#fff', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0f172a' }}>{editingExpense ? "Edit Expense" : "Add Upcoming Payment"}</h3>
                {!editingExpense && <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#64748b' }}>After saving, use the Pay button to mark as paid.</p>}
              </div>
              <button onClick={() => { setShowForm(false); resetForm(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#94a3b8' }}>✕</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Category *</label>
                  <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} required>
                    {EXPENSE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Amount (₹) *</label>
                  <input type="number" step="0.01" min={0} value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} required />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Description</label>
                <input type="text" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="e.g., Office rent for January" style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Expense Date *</label>
                  <input type="date" value={formData.expense_date} onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} required />
                </div>
                {editingExpense && isPaid(editingExpense) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b' }}>Payment Date</label>
                    <input type="date" value={formData.payment_date} readOnly style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f1f5f9' }} />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.is_recurring} onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                  Recurring Expense
                </label>
                {formData.is_recurring && (
                  <select value={formData.recurring_frequency} onChange={(e) => setFormData({ ...formData, recurring_frequency: e.target.value })} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', flex: 1 }}>
                    {RECURRING_FREQUENCIES.map(freq => <option key={freq} value={freq}>{freq.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                  </select>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 700 }}>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <Button variant="ghost" type="button" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
                <Button variant="primary" type="submit">{editingExpense ? "Update Expense" : "Save Upcoming Payment"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-state">Loading expenses...</div>
      ) : filteredExpenses.length === 0 ? (
        <div className="empty-state">
          {paidTab === "paid"
            ? "No paid expenses found."
            : paidTab === "unpaid"
              ? "No unpaid expenses found."
              : "No expenses found. Click \"Add Expense\" to create one."}
        </div>
      ) : (
        <div className="mod-table-wrapper" style={{ overflowX: "auto", borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'var(--mod-shadow-sm)', background: '#fff' }}>
          <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '16px', textAlign: 'left', color: '#475569', fontWeight: 700 }}>Date</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#475569', fontWeight: 700 }}>Category</th>
                <th style={{ padding: '16px', textAlign: 'left', color: '#475569', fontWeight: 700 }}>Description</th>
                <th style={{ padding: '16px', textAlign: 'right', color: '#475569', fontWeight: 700 }}>Amount</th>
                <th style={{ padding: '16px', textAlign: 'center', color: '#475569', fontWeight: 700 }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', color: '#475569', fontWeight: 700 }}>Recur</th>
                <th style={{ padding: '16px', textAlign: 'right', color: '#475569', fontWeight: 700 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map(ex => (
                <tr key={ex.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '16px', color: '#64748b' }}>{formatDate(ex.expense_date)}</td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ background: '#f1f5f9', color: '#334155', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 700 }}>
                      {ex.category}
                    </span>
                  </td>
                  <td style={{ padding: '16px', color: '#0f172a' }}>{ex.description || "-"}</td>
                  <td style={{ padding: '16px', textAlign: 'right', fontWeight: 800, color: '#dc2626' }}>{formatCurrency(ex.amount)}</td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {isPaid(ex) ? (
                      <span style={{ color: '#166534', background: '#dcfce7', padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 800 }}>✓ PAID</span>
                    ) : (
                      <span style={{ color: '#b45309', background: '#fef3c7', padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 800 }}>⏳ UNPAID</span>
                    )}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    {ex.is_recurring ? (
                      <span style={{ color: '#0ea5e9' }} title={ex.recurring_frequency}>🔄</span>
                    ) : "-"}
                  </td>
                  <td style={{ padding: '16px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                      {!isPaid(ex) && (
                        <Button variant="primary" size="sm" onClick={() => handleMarkAsPaid(ex)}>Pay</Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleEdit(ex)}>Edit</Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(ex.id)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
